import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useResellerFromSubdomain } from '../../../models/hooks/useResellerFromSubdomain';
import { AppBar as RetroAppBar } from '../AppBar';
import { CustomerImpersonationWarning } from '../CustomerImpersonationWarning';
import { ManagedAccountAccessWarning } from '../ManagedAccountAccessWarning';
import { MainContainerBackground } from '../MainContainerBackground';
import { NavBar as RetroNavBar } from '../NavBar';
import { INavBarLink } from '../NavBarLink';
import { ResellerCompactLogoIcon } from '../svgs/icons/ResellerCompactLogoIcon';
import { ResellerLogoIcon } from '../svgs/icons/ResellerLogoIcon';
import { useCoffeeNavLinks } from './hooks/useCoffeeNavLinks';
import { useIsCoffeeNavCompact } from './hooks/useIsCompactNav';
import { styleSheet } from './styles';

const CoffeeAppBarUserLinks: INavBarLink[] = [
	{
		linkName: 'Schedule a Meeting',
		path: '/schedule-meeting',
	},
	{
		linkName: 'Settings',
		path: '/settings',
	},
	{
		linkName: 'Logout',
		path: '/logout',
	},
];

const CoffeeAppBarAdminLinks: INavBarLink[] = [
	{
		linkName: 'Invite Teammates',
		path: '/settings/manage-users',
	},
	...CoffeeAppBarUserLinks,
];

export function CoffeeAppShell({
	children,
	className,
	styles = [],
}: {
	className?: string;
	children: React.ReactNode;
	styles?: StyleDeclarationValue[];
}) {
	const userSession = useUserSession();
	const showingCompactNav = useIsCoffeeNavCompact();
	const links = useCoffeeNavLinks();
	const reseller = useResellerFromSubdomain(userSession?.account?.reseller);
	const onRenderNavBarLogo = React.useCallback(
		(compact: boolean) => {
			const coffee = (
				<div className={css(styleSheet.coffeeLogoContainer)}>
					<NavLink to='/dashboard'>
						<ResellerLogoIcon className={css(styleSheet.mainLogo)} reseller={reseller} />
					</NavLink>
				</div>
			);
			const compactLogo = (
				<NavLink to='/dashboard'>
					<ResellerCompactLogoIcon reseller={reseller} />
				</NavLink>
			);
			return compact ? compactLogo : coffee;
		},
		[reseller]
	);

	return (
		<MainContainerBackground
			className={`coffee-main-container main-container ${className} ${css(styleSheet.coffeeContainer, ...styles)}`}
		>
			<RetroNavBar
				className={`coffee-main-container-nav ${css(
					styleSheet.coffeeNavbar,
					showingCompactNav ? styleSheet.coffeeNavBarCompact : null
				)}`}
				compactLayout={showingCompactNav}
				links={links}
				onRenderLogo={onRenderNavBarLogo}
			/>
			<div
				className={`coffee-main-container-right-container ${css(
					styleSheet.coffeeRightContainer,
					showingCompactNav ? styleSheet.coffeeRightContainerExpanded : null
				)}`}
			>
				<div className={css(styleSheet.warningStack)}>
					<CustomerImpersonationWarning fixed={false} userSession={userSession} />
					<ManagedAccountAccessWarning fixed={false} userSession={userSession} />
				</div>
				<RetroAppBar
					className={`coffee-main-container-header ${css(
						styleSheet.coffeeAppBar,
						showingCompactNav ? styleSheet.coffeeAppBarExpanded : null
					)}`}
					style={{ background: 'transparent' }}
					coffeeLinks={userSession.account.isAdmin ? CoffeeAppBarAdminLinks : CoffeeAppBarUserLinks}
				/>
				<div className={`coffee-main-container-right-container-content ${css(styleSheet.coffeeContentContainer)}`}>
					{children}
				</div>
			</div>
		</MainContainerBackground>
	);
}

export { MainContainerPageShell } from './MainContainerPageShell';
